import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { inr } from "@/lib/api";
import api from "@/lib/api";
import { Plus, Eye, Lock, Clock, Bell, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

const catBadge = {
  health: "bg-olive/10 text-olive",
  education: "bg-plum/10 text-plum",
  personal_care: "bg-gold/15 text-gold-deep",
  beauty: "bg-gold/15 text-gold-deep",
  combo: "bg-gold/15 text-gold-deep",
  fashion: "bg-rose/10 text-rose",
  agriculture: "bg-olive/10 text-olive",
};

/** Product card
 *  - Visitors (not logged in): image + name only. No MRP/SP badges. Clicking prompts login.
 *  - Associates (logged in): SP badge, DP price and Add-to-Cart button.
 *  - `hideSp` forces SP badge off (used on Home).
 *  - `hideMrp` hides ALL price info (home slider — required by HP-02 of the functional doc).
 *  - `comingSoon` (or product.is_coming_soon) badges the product and disables purchase.
 */
export default function ProductCard({ product, hideSp = false, hideMrp = false, comingSoon }) {
  const { add } = useCart();
  const { user } = useAuth();
  const sp = product.sp ?? product.sp;
  const showSp = user && !hideSp && !product.is_coming_soon && !comingSoon;
  const isComingSoon = comingSoon || product.is_coming_soon;
  const showPrice = !hideMrp && !isComingSoon;

  return (
    <div data-testid={`product-card-${product.id}`} className="group bg-white border border-line hover:border-gold/50 hover:shadow-md transition-all rounded-sm overflow-hidden flex flex-col">
      <Link
        to={isComingSoon ? "#" : user ? `/product/${product.id}` : "/login"}
        onClick={(e) => { if (isComingSoon) { e.preventDefault(); toast.info(`${product.name} — Coming Soon`); } }}
        className="block relative aspect-square bg-ivory overflow-hidden"
      >
        <img
          src={product.image
            ? `${product.image}${product.image.includes("?") ? "&" : "?"}v=${product.updated_at || product.id}`
            : "/product-images/coming-soon.svg"}
          alt={product.name}
          onError={(e) => { e.currentTarget.src = "/product-images/coming-soon.svg"; }}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <span className={`absolute top-3 left-3 ${catBadge[product.category] || "bg-royal/10 text-royal"} text-[10px] uppercase tracking-widest px-2 py-1`}>
          {product.category.replace("_", " ")}
        </span>
        {showSp && (
          <span data-testid={`sp-badge-${product.id}`} className="absolute top-3 right-3 bg-royal text-gold text-[10px] font-bold px-2 py-1">
            {Number.isInteger(sp) ? sp : Number(sp).toFixed(2)} SP
          </span>
        )}
        {isComingSoon && (
          <span data-testid={`coming-soon-${product.id}`} className="absolute inset-x-0 bottom-0 bg-royal/95 text-gold text-[10px] font-semibold uppercase tracking-widest px-3 py-2 inline-flex items-center justify-center gap-1.5">
            <Clock size={11} /> Coming Soon
          </span>
        )}
      </Link>
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-[10px] text-navy/40 uppercase tracking-widest">{product.sku}</div>
        <Link
          to={isComingSoon ? "#" : user ? `/product/${product.id}` : "/login"}
          onClick={(e) => { if (isComingSoon) { e.preventDefault(); toast.info(`${product.name} — Coming Soon`); } }}
          className="font-serif text-lg text-royal mt-1 line-clamp-2 leading-tight hover:text-gold transition-colors"
        >
          {product.name}
        </Link>
        {showPrice && (
          <div className="mt-3 flex items-end gap-2">
            {user ? (
              <>
                <span className="text-xl font-semibold text-royal">{inr(product.dp)}</span>
                {product.mrp > product.dp && <span className="text-xs text-navy/40 line-through">{inr(product.mrp)}</span>}
              </>
            ) : (
              <span className="text-xl font-semibold text-royal">{inr(product.mrp)}</span>
            )}
          </div>
        )}
        <div className="mt-4 flex gap-2">
          {isComingSoon ? (
            <ComingSoonSubscribe productId={product.id} productName={product.name} />
          ) : user ? (
            <button
              data-testid={`add-cart-${product.id}`}
              onClick={() => { add(product); toast.success(`${product.name} added to cart`); }}
              className="flex-1 bg-royal text-white text-xs py-2 inline-flex items-center justify-center gap-1.5 hover:bg-royal-deep transition-colors rounded-sm"
            >
              <Plus size={14} /> Add
            </button>
          ) : (
            <Link
              to={`/product/${product.id}`}
              data-testid={`view-product-${product.id}`}
              className="flex-1 bg-royal/[0.06] border border-royal/25 text-royal text-xs py-2 inline-flex items-center justify-center gap-1.5 hover:bg-royal hover:text-white transition-colors rounded-sm"
            >
              <Eye size={12} /> View details
            </Link>
          )}
          <Link
            to={isComingSoon ? "#" : user ? `/product/${product.id}` : "/login"}
            onClick={(e) => { if (isComingSoon) e.preventDefault(); }}
            className="px-3 py-2 border border-line hover:border-gold text-royal text-xs rounded-sm inline-flex items-center gap-1"
          >
            <Eye size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Inline "Notify Me" form shown on Coming Soon products.
 *  Collapses to a compact "Coming Soon" button until clicked.
 */
function ComingSoonSubscribe({ productId, productName }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${productId}/notify-me`, { email });
      toast.success(data?.message || "We'll email you");
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid={`cs-subscribed-${productId}`} className="flex-1 bg-olive/10 border border-olive/30 text-olive text-[11px] py-2 inline-flex items-center justify-center gap-1.5 rounded-sm">
        <CheckCircle2 size={12} /> You&apos;re on the list
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); setOpen(true); }}
        data-testid={`notify-me-open-${productId}`}
        className="flex-1 bg-royal/[0.06] border border-royal/25 text-royal text-xs py-2 inline-flex items-center justify-center gap-1.5 hover:bg-royal hover:text-white transition-colors rounded-sm"
      >
        <Bell size={12} /> Notify me
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="flex-1 flex gap-1" onClick={(e) => e.stopPropagation()}>
      <input
        type="email"
        required
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        data-testid={`notify-me-email-${productId}`}
        className="flex-1 min-w-0 border border-royal/30 px-2 text-xs rounded-sm focus:border-gold focus:outline-none"
      />
      <button
        type="submit"
        disabled={busy}
        data-testid={`notify-me-submit-${productId}`}
        className="bg-royal text-white text-xs px-2.5 py-2 rounded-sm hover:bg-royal-deep disabled:opacity-50"
        title={`Ping me when ${productName} goes live`}
      >
        {busy ? "…" : "Ping"}
      </button>
    </form>
  );
}
